'use client'

import { CodeEditor } from '@/components/shared/code-editor'
import { cn } from '@/lib/utils'
import { useEffect, useRef, useState } from 'react'

const step1 = `
import { VendureConfig } from '@vendure/core'

export const config: VendureConfig = {
    // customize the API paths 
    apiOptions: {
        port: 3000,
        adminApiPath: '/management-api',
        shopApiPath: '/storefront-api',
    },
}`

const step2 = `
import { VendureConfig } from '@vendure/core'

export const config: VendureConfig = {
    apiOptions: {
        port: 3000,
        adminApiPath: '/management-api',
        shopApiPath: '/storefront-api',
    },
    // customize the token expiry time
    authOptions: {
        token: {
            expiry: 60 * 60 * 24 * 30, // 30 days
        },
    },
}`

const step3 = `
import { VendureConfig } from '@vendure/core'

export const config: VendureConfig = {
    apiOptions: {
        port: 3000,
        adminApiPath: '/management-api',
        shopApiPath: '/storefront-api',
    },
    authOptions: {
        token: {
            expiry: 60 * 60 * 24 * 30, // 30 days
        },
    },
    // add custom fields 
    customFields: {
        Product: [
            {
                name: 'recommendations',
                type: 'relation',
                entity: 'Product',
                list: true,
            },
        ],
    },
}`

export function CodeFirstConfiguration({ className }: { className?: string }) {
  const [fileContent, setFileContent] = useState(step1)
  const [inView, setInView] = useState(false)
  const animationStartedRef = useRef(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const timersRef = useRef<NodeJS.Timeout[]>([])

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        const isIntersecting = entry.isIntersecting
        setInView(isIntersecting)

        // When scrolled out of view, clear timers and reset
        if (!isIntersecting) {
          // Clear any pending timers
          timersRef.current.forEach(clearTimeout)
          timersRef.current = []

          // Reset content to step 1
          setFileContent(step1)
          animationStartedRef.current = false
        }
      },
      { threshold: 0.1, rootMargin: '50px' },
    )

    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => {
      timersRef.current.forEach(clearTimeout)
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    // Start animation sequence when component comes into view
    if (inView && !animationStartedRef.current) {
      // Set flag to prevent animation from starting multiple times
      animationStartedRef.current = true

      console.log('Animation starting - component in view')

      // Clear any existing timers
      timersRef.current.forEach(clearTimeout)

      // Set content to step 1 initially
      setFileContent(step1)

      // Create new timers for step 2 and 3
      const timer1 = setTimeout(() => {
        console.log('Applying step 2')
        setFileContent(step2)
      }, 1500)

      const timer2 = setTimeout(() => {
        console.log('Applying step 3')
        setFileContent(step3)
      }, 3000)

      // Store timer IDs for cleanup
      timersRef.current = [timer1, timer2]
    }
  }, [inView])

  return (
    <div
      ref={containerRef}
      className={cn('w-full', className)}
    >
      <CodeEditor
        className="min-h-[700px] text-sm lg:text-base"
        closeTab={() => {}}
        tabs={[
          {
            filename: 'vendure-config.ts',
            content: fileContent.trim(),
          },
        ]}
      />
    </div>
  )
}
